import React, { useState, memo, useMemo, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import {
  vscDarkPlus,
  oneLight,
} from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check } from 'lucide-react';
import rehypeRaw from 'rehype-raw';
import toast from 'react-hot-toast';
import { FiEye, FiLoader } from 'react-icons/fi';

import AuthImage from './chat/AuthImage';
import { appImages } from '../utils/constants';
import ExcelPreviewModal from './chat/modals/ExcelPreviewModal';
import QuestionsCard, { parseQuestions } from './chat/QuestionsCard';
import DashboardLinkCard, { dashboardPath } from './chat/DashboardLinkCard';
import { API_URL } from '../services/api';
import { clearLocalStorageKeepingLoginHint } from '../config/authStorageKeys';

// Hoisted to module scope — stable references so React.memo is never defeated
const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [rehypeKatex, rehypeRaw];

const NotificationChip = ({ event, app }) => {
  // `app` here is whatever value an upstream `<event app="...">` tag
  // literally carries -- for a meeting brought to chat that's 'meeting' or
  // 'manual_meeting' (createNewTaskForNotification / MeetingDetailModal.jsx
  // / Ui.js's meetingContext handler all use one of those, since `app` also
  // does double duty there distinguishing manual vs Teams-synced meetings).
  // appImages only has an entry for 'microsoft_teams', so either value
  // looked up directly resolves to undefined -- React then omits the <img>
  // src attribute entirely, which is exactly a broken image, not a missing
  // one. Normalize here, at the point the icon is actually chosen, rather
  // than at the several places that set `app`, since those still need the
  // original value for their own (non-decorative) meaning.
  const iconKey =
    app === 'meeting' || app === 'manual_meeting' ? 'microsoft_teams' : app;
  return (
    <div className="animate-fade-in-fast group relative flex h-8 items-center gap-2 rounded-full border border-zinc-700 bg-zinc-800 p-1.5 pr-3 text-xs text-zinc-300 shadow-md">
      <img
        src={appImages[iconKey] || appImages.agent}
        alt={app}
        className="h-5 w-5 rounded-full"
      />
      <button className="max-w-[200px] flex-1 truncate text-left">
        <span className="font-medium">{event}</span>
      </button>
    </div>
  );
};

function CodeBlockWithCopy({
  language,
  codeString,
  syntaxHighlighterProps,
  lightTheme = false,
}) {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    if (isCopied) return;

    navigator.clipboard
      .writeText(codeString)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      })
      .catch((err) => {
        toast.error('Failed to copy code to clipboard');
      });
  };

  return (
    <div className="code-block-container group relative">
      {/* Copy Button */}
      <button
        onClick={handleCopy}
        aria-label={isCopied ? 'Code copied' : 'Copy code'}
        className={`absolute right-2 top-2 z-10 rounded-md p-1.5 transition-opacity duration-200 ease-in-out
                    ${
                      isCopied
                        ? 'bg-green-700 text-white'
                        : 'bg-gray-700 text-gray-300 opacity-0 hover:bg-gray-600 focus:opacity-100 group-hover:opacity-100'
                    }`}
      >
        {isCopied ? <Check size={16} /> : <Copy size={16} />}
      </button>

      {/* Syntax Highlighter */}
      <SyntaxHighlighter
        style={lightTheme ? oneLight : vscDarkPlus} // Use oneLight for light theme, vscDarkPlus for dark theme
        language={language}
        PreTag="div" // You are using "div" as the PreTag.
        {...syntaxHighlighterProps} // Pass down any props received
        customStyle={{
          border: '0.1px solid #555',
          // Styles here apply to the PreTag="div" container
          // Padding adjustment might be needed if copy button overlaps too much
          // paddingTop: '2.5rem', // Uncomment and adjust if needed
        }}
        wrapLines={true} // Enable line wrapping (applies white-space: pre-wrap)
        codeTagProps={{
          // Props for the <code> tag that wraps the actual code content
          style: {
            // Ensures long unbroken strings within a line will also break and wrap.
            // overflowWrap: 'break-word', // Standard word breaking
            // Alternatively, for more aggressive breaking:
            // wordBreak: 'break-all',
            // `white-space: 'pre-wrap'` should be handled by `wrapLines={true}`.
            // If you find it's not, you can explicitly add it here:
            // whiteSpace: 'pre-wrap',
          },
        }}
      >
        {codeString}
      </SyntaxHighlighter>
    </div>
  );
}

const MarkdownRenderer = memo(function MarkdownRenderer({
  markdown,
  lightTheme = false,
  isAssistant = false,
  userGoogleId,
}) {
  const [isCopied, setIsCopied] = useState(false);
  const userGoogleIdRef = useRef(userGoogleId);
  userGoogleIdRef.current = userGoogleId;
  const [processingFileId, setProcessingFileId] = useState(null);
  const [excelPreviewModal, setExcelPreviewModal] = useState({
    isOpen: false,
    file: null,
  });

  const getFileForPreview = useCallback(async (accessToken, url) => {
    let fetchUrl = url.startsWith('/') ? `${API_URL}${url}` : url;

    const isDirectS3 = url.includes('amazonaws.com');

    // If it's a direct S3 link, use the backend proxy to bypass Amazon's CORS restrictions
    if (isDirectS3) {
      fetchUrl = `${API_URL}/file/stream-s3?s3_url=${encodeURIComponent(url)}`;
    } else {
      // Automatically extract MongoDB ObjectId (24 hex characters) from the URL
      // BUT only do URL rewrites if it's NOT a direct S3 link (because the S3 file ID likely doesn't match the parent file table)
      const idMatch = url.match(/([a-fA-F0-9]{24})/);

      if (idMatch && (url.includes('/file/download') || url.startsWith('/'))) {
        fetchUrl = `${API_URL}/file/download-direct/${idMatch[1]}`;
      }
    }

    // Since we are now routing S3 links through our backend proxy as well, we ALWAYS send the Authorization header
    // and include credentials (unless somehow dealing with an unrecognized external domain).
    const isExternal =
      !fetchUrl.includes(API_URL) &&
      !fetchUrl.startsWith('/') &&
      !fetchUrl.includes(window.location.hostname);

    const options = {
      method: 'GET',
      headers:
        accessToken && !isExternal
          ? { Authorization: `Bearer ${accessToken}` }
          : {},
      credentials: isExternal ? 'omit' : 'include',
    };

    try {
      let response = await fetch(fetchUrl, options);

      if (response.status === 401 && !isExternal) {
        clearLocalStorageKeepingLoginHint();
        window.location.href = '/login';
        return;
      }
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(
          `Error fetching file: ${response.statusText}. ${errorText}`,
        );
      }

      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        // If the backend returns our JSON wrapper with a presigned URL instead of the blob
        const json = await response.json();
        if (json.url) {
          response = await fetch(json.url);
          if (!response.ok) throw new Error('Could not fetch presigned URL.');
        } else {
          throw new Error('JSON response did not contain a url field.');
        }
      } else if (
        contentType &&
        contentType.includes('text/html') &&
        !isExternal
      ) {
        // Stop it from passing HTML into the file parser
        throw new Error(
          'Received HTML instead of a file blob from the server. The link might be invalid.',
        );
      }

      return response.blob();
    } catch (error) {
      throw error;
    }
  }, []);

  const handleExcelPreview = useCallback(
    async (href, filename) => {
      setProcessingFileId(href);
      try {
        const blob = await getFileForPreview(userGoogleIdRef.current, href);
        const fileObject = new File([blob], filename, {
          type:
            blob.type ||
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        setExcelPreviewModal({ isOpen: true, file: fileObject });
      } catch (error) {
        toast.error('Could not load preview.');
      } finally {
        setProcessingFileId(null);
      }
    },
    [getFileForPreview],
  );

  const closeExcelPreview = () => {
    setExcelPreviewModal({ isOpen: false, file: null });
  };

  const handleCopyToClipboard = () => {
    if (isCopied) return;

    navigator.clipboard
      .writeText(markdown)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
      })
      .catch((err) => {
        toast.error('Failed to copy markdown to clipboard');
      });
  };

  // Memoize the components object so ReactMarkdown doesn't re-parse
  // when parent re-renders with the same markdown text.
  const markdownComponents = useMemo(
    () => ({
      code({ node, inline, className, children, ...props }) {
        const match = /language-(\w+)/.exec(className || '');
        const codeString = String(children).replace(/\n$/, '');

        if (!inline && match?.[1] === 'questions') {
          // The agent's follow-up questions; a malformed block stays code.
          const questions = parseQuestions(codeString);
          if (questions) return <QuestionsCard questions={questions} />;
        }

        if (!inline && match) {
          return (
            <CodeBlockWithCopy
              language={match?.[1]}
              codeString={codeString}
              syntaxHighlighterProps={props}
              lightTheme={lightTheme}
            />
          );
        }

        return (
          <code
            className="rounded border border-zinc-700/50 bg-zinc-800/60 px-1.5 py-0.5 font-mono text-[14px] text-[#e879f9]"
            {...props}
          >
            {children}
          </code>
        );
      },

      a({ node, href, children, ...props }) {
        const linkText = String(children);

        const dashboard = dashboardPath(href);
        if (dashboard) {
          return (
            <DashboardLinkCard path={dashboard}>{children}</DashboardLinkCard>
          );
        }

        // Check if the text or the href ends with .xlsx or .csv
        const isExcelText =
          linkText.toLowerCase().includes('.xlsx') ||
          linkText.toLowerCase().includes('.csv');
        const isExcelHref =
          href &&
          (href.toLowerCase().split('?')[0].endsWith('.xlsx') ||
            href.toLowerCase().split('?')[0].endsWith('.csv'));

        if (isExcelText || isExcelHref) {
          const isProcessing = processingFileId === href;
          return (
            <span className="group my-1 inline-flex cursor-pointer items-center gap-2 rounded border border-[#60a5fa]/30 bg-[#60a5fa]/10 px-2 py-0.5 align-middle transition-colors hover:bg-[#60a5fa]/20">
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-[#60a5fa] transition-colors hover:text-[#93c5fd]"
                {...props}
              >
                {children}
              </a>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleExcelPreview(
                    href,
                    linkText.replace(/[^a-zA-Z0-9_.-]/g, ''),
                  );
                }}
                disabled={processingFileId !== null}
                className="ml-1 text-[#60a5fa] opacity-60 transition-opacity disabled:opacity-40 group-hover:opacity-100"
                title="Preview Excel File"
              >
                {isProcessing ? (
                  <FiLoader className="animate-spin" />
                ) : (
                  <FiEye size={16} />
                )}
              </button>
            </span>
          );
        }

        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#60a5fa] underline decoration-[#60a5fa]/40 underline-offset-2 transition-colors hover:text-[#93c5fd] hover:decoration-[#60a5fa]"
            {...props}
          >
            {children}
          </a>
        );
      },

      img({ node, src, alt, ...props }) {
        return <AuthImage src={src} alt={alt || ''} {...props} />;
      },

      event({ node, ...props }) {
        const eventText = props.event || 'Event';
        const appText = props.app || 'App';

        return (
          <span className="inline-block align-middle">
            <NotificationChip event={eventText} app={appText} />
          </span>
        );
      },

      h1({ children }) {
        return (
          <h1 className="mb-3 mt-6 text-[26px] font-bold leading-[1.3] text-gray-100">
            {children}
          </h1>
        );
      },

      h2({ children }) {
        return (
          <h2 className="mb-3 mt-5 text-[22px] font-bold leading-[1.35] text-gray-100">
            {children}
          </h2>
        );
      },

      h3({ children }) {
        return (
          <h3 className="mb-2.5 mt-4 text-[18px] font-semibold leading-[1.4] text-gray-200">
            {children}
          </h3>
        );
      },

      h4({ children }) {
        return (
          <h4 className="mb-2 mt-3.5 text-[16px] font-semibold leading-[1.45]">
            {children}
          </h4>
        );
      },

      h5({ children }) {
        return (
          <h5 className="mb-2 mt-3 text-[15px] font-semibold leading-[1.5]">
            {children}
          </h5>
        );
      },

      h6({ children }) {
        return (
          <h6 className="mb-2 mt-3 text-[14px] font-semibold uppercase leading-[1.5] tracking-wide">
            {children}
          </h6>
        );
      },

      p({ children }) {
        return <span className="block leading-[1.7]">{children}</span>;
      },

      strong({ children }) {
        return <strong className="font-medium">{children}</strong>;
      },

      em({ children }) {
        return <em className="italic">{children}</em>;
      },

      del({ children }) {
        return <del className="line-through">{children}</del>;
      },

      ul({ children }) {
        return (
          <ul className="mb-4 ml-6 list-outside list-disc space-y-1.5">
            {children}
          </ul>
        );
      },

      ol({ children }) {
        return (
          <ol className="mb-4 ml-6 list-outside list-decimal space-y-1.5">
            {children}
          </ol>
        );
      },

      li({ children }) {
        return <li className="pl-1 leading-[1.7]">{children}</li>;
      },

      blockquote({ children }) {
        return (
          <blockquote className="my-4 border-l-[3px] border-gray-600 bg-zinc-800/40 py-2.5 pl-4 pr-4 text-gray-300/90">
            {children}
          </blockquote>
        );
      },

      hr() {
        return <hr className="my-6 border-0 border-t border-gray-700/50" />;
      },

      kbd({ children }) {
        return (
          <kbd className="rounded border border-gray-700/60 bg-zinc-800/70 px-2 py-0.5 font-mono text-[13px] font-medium text-gray-300 shadow-sm">
            {children}
          </kbd>
        );
      },

      table({ children, ...props }) {
        return (
          <div className="my-6 overflow-x-auto rounded-lg border border-zinc-700">
            <table
              className="w-full border-collapse text-left text-sm"
              {...props}
            >
              {children}
            </table>
          </div>
        );
      },

      thead({ children, ...props }) {
        return (
          <thead
            className="border-b border-zinc-700 bg-[#1f1f23] text-xs font-semibold uppercase text-zinc-300"
            {...props}
          >
            {children}
          </thead>
        );
      },

      tbody({ children, ...props }) {
        return (
          <tbody
            className="divide-y divide-zinc-700/50 bg-[#18181b]"
            {...props}
          >
            {children}
          </tbody>
        );
      },

      tr({ children, ...props }) {
        return (
          <tr className="transition-colors hover:bg-zinc-800/40" {...props}>
            {children}
          </tr>
        );
      },

      th({ children, ...props }) {
        return (
          <th
            className="whitespace-nowrap border-b border-zinc-700 py-3"
            style={{
              paddingLeft: '20px',
              paddingRight: '16px',
              ...(props.style || {}),
            }}
            {...props}
          >
            {children}
          </th>
        );
      },

      td({ children, ...props }) {
        let textContent = '';
        React.Children.forEach(children, (child) => {
          if (typeof child === 'string') {
            textContent += child;
          }
        });

        const trimmedText = textContent.trim().toLowerCase();
        const isColor = [
          'red',
          'blue',
          'green',
          'yellow',
          'orange',
          'purple',
          'black',
          'white',
          'gray',
          'pink',
          'brown',
          'cyan',
          'magenta',
        ].includes(trimmedText);

        const customStyle = {
          paddingLeft: '20px',
          paddingRight: '16px',
          ...(props.style || {}),
        };

        if (isColor) {
          customStyle.backgroundColor = trimmedText;
          customStyle.color = ['yellow', 'white', 'pink', 'cyan'].includes(
            trimmedText,
          )
            ? 'black'
            : 'white';
        }

        return (
          <td className="py-3 align-top" style={customStyle} {...props}>
            {children}
          </td>
        );
      },
    }),
    [lightTheme, processingFileId, handleExcelPreview],
  );

  return (
    <div className="group relative">
      <div
        className={`markdown-container prose rounded-md p-1 md:prose-sm lg:prose-sm  ${!lightTheme && 'prose-invert'} max-w-none text-[#f0f0ee]`}
      >
        <ReactMarkdown
          className="text-[15px] leading-[1.7]"
          remarkPlugins={REMARK_PLUGINS}
          rehypePlugins={REHYPE_PLUGINS}
          components={markdownComponents}
        >
          {markdown}
        </ReactMarkdown>
      </div>

      {isAssistant && !lightTheme && (
        <div className="absolute -bottom-1 -right-1 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            onClick={handleCopyToClipboard}
            aria-label={isCopied ? 'Content copied' : 'Copy content'}
            className={`rounded-md p-1.5 ${
              isCopied
                ? 'bg-green-700 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {isCopied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
      )}
      <ExcelPreviewModal
        isOpen={excelPreviewModal.isOpen}
        onClose={closeExcelPreview}
        file={excelPreviewModal.file}
      />
    </div>
  );
});

export default MarkdownRenderer;
